import React, { useCallback, useContext, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { ParcelSearchContext } from './ParcelSearchContext';
import ParcelSearchForm from './ParcelSearchForm/ParcelSearchForm';
import ResultsGrid from './ResultsGrid/ResultsGrid';

import './HomeView.scss';
import { Box, Button } from '@mui/material';
import { useSnackbar, VariantType } from 'notistack';

export default function HomeView() {
    const PAGING_DEBOUNCE_DELAY_MILLISECONDS = 100;
    const SCROLL_FETCH_THRESHOLD = 0.5;
    const SCROLL_POSITION_CONDENSE_THRESHOLD = 100;
    const SCROLL_POSITION_EXPAND_THRESHOLD = SCROLL_POSITION_CONDENSE_THRESHOLD * 3;
    const SCROLL_POSITION_TOP = 0;

    const { shouldShowLoading, parcels, parcelListError, warningText, handleResultsPaging, snackbarKeys, setSnackbarKeys } =
        useContext(ParcelSearchContext);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    if (parcelListError) throw parcelListError; // We throw here so we can get caught by the Error Boundary

    const [component, setComponent] = useState(<></>);
    const [lastScrollPosition, setLastScrollPosition] = useState(0);
    const [shouldCondenseForm, setShouldCondenseForm] = useState(false);
    const [ableToExpand, setAbleToExpand] = useState(false);

    useEffect(() => {
        let tmpComponent = <></>;
        let message = '';
        let variant: VariantType = 'default';

        if (parcels && parcels?.length === 0) {
            message = 'No results for your search.';
            if (warningText) {
                message += ' ' + warningText;
            }
            variant = 'warning';
        }
        if (parcels && parcels?.length > 0)
            tmpComponent = (
                <ResultsGrid shouldCondenseForm={shouldCondenseForm} updateFormSizeHandler={updateFormSizeHandler} />
            );
        if (shouldShowLoading) {
            message = 'Loading results...';
            variant = 'loading';
        }

        if (message) {
            const snackbarKey = enqueueSnackbar(message, {
                variant,
                autoHideDuration: null,
                preventDuplicate: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });

            if (!snackbarKeys) {
                setSnackbarKeys({ [variant]: [snackbarKey] });
            } else {
                const variantSnackbarKeys = snackbarKeys[variant];
                if (variantSnackbarKeys) {
                    setSnackbarKeys({ ...snackbarKeys, [variant]: [...variantSnackbarKeys, snackbarKey] });
                } else {
                    setSnackbarKeys({ ...snackbarKeys, [variant]: [snackbarKey] });
                }
            }
        }

        setComponent(tmpComponent);
    }, [parcels, shouldShowLoading, warningText, ableToExpand]);

    const debounceHandleScroll = debounce((element) => {
        if (element) {
            const scrollTarget = element;
            const scrollPercentage = Number((scrollTarget.scrollTop / scrollTarget.scrollHeight).toFixed(2));
            const isPastThreshold = scrollPercentage >= SCROLL_FETCH_THRESHOLD;
            const isScrollingDown = lastScrollPosition < scrollTarget.scrollTop;

            setLastScrollPosition(scrollTarget.scrollTop);

            if (isScrollingDown && isPastThreshold) {
                handleResultsPaging();
            }
        }
    }, PAGING_DEBOUNCE_DELAY_MILLISECONDS);

    const updateFormSizeHandler = useCallback(
        async (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const scrollPosition = containerRefElement.scrollTop;

                if (scrollPosition > SCROLL_POSITION_CONDENSE_THRESHOLD) {
                    setShouldCondenseForm(true);
                }

                if (scrollPosition > SCROLL_POSITION_EXPAND_THRESHOLD) {
                    setAbleToExpand(true);
                }

                if (ableToExpand && scrollPosition === SCROLL_POSITION_TOP) {
                    setShouldCondenseForm(false);
                    setAbleToExpand(false);
                }

                debounceHandleScroll(containerRefElement);
            }
        },
        [parcels, ableToExpand],
    );

    return (
        <Box className="home-view">
            <Box className="home-view__form-container">
                <ParcelSearchForm shouldCondense={shouldCondenseForm} />
            </Box>
            {component}
        </Box>
    );
}
